import { RefObject } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { TypedFormatMessageFn } from 'types/intl';

export interface IEvidenceUploadDropzoneProps {
  inputRef: RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilesSelected: (files: File[]) => void | Promise<void>;
  accept: string;
  maxFileSizeMB: number;
}

export function EvidenceUploadDropzone({
  inputRef,
  onFileChange,
  onFilesSelected,
  accept,
  maxFileSizeMB,
}: IEvidenceUploadDropzoneProps) {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();

  const handleDashedAreaClick = () => {
    inputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files?.length) {
      onFilesSelected(Array.from(files));
    }
  };

  return (
    <>
      <input ref={inputRef} type='file' accept={accept} multiple className='hidden' onChange={onFileChange} />
      <Flex
        align='center'
        justify='center'
        role='button'
        tabIndex={0}
        onClick={handleDashedAreaClick}
        onKeyDown={e => e.key === 'Enter' && handleDashedAreaClick()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className='w-full cursor-pointer rounded-md border border-dashed border-gray-200 bg-black/[0.02] p-4'
      >
        <Flex vertical align='center' className='gap-4'>
          <Flex align='center' justify='center' className='h-12 w-12 p-1.5 box-border'>
            <InboxOutlined className='text-4xl leading-none text-gray-400' />
          </Flex>
          <Flex vertical align='center' className='gap-1'>
            <Typography.Text className='font-normal text-base leading-6 text-black/90'>
              {formatMessage({ id: 'evidence.upload.dropzone.title' })}
            </Typography.Text>
            <Typography.Text className='text-center font-normal text-xs leading-5 text-black/50'>
              {formatMessage({ id: 'evidence.upload.dropzone.supportedTypes' })}
              <br />
              {formatMessage({ id: 'evidence.upload.dropzone.maxFileSize' }, { maxFileSizeMB })}
            </Typography.Text>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
